import React, { useState, useEffect, useContext, useRef } from 'react'
import Context from '../utils/Context'
import ROS from '../utils/ROS/ROS'
import { 
  subscribeToRobotPosition, 
  subscribeToOccupancyMap, 
  subscribeToLaserData as subscribeToLaserTopic, 
  createPoseEstimatePublisher,
  publishPoseEstimate,
  unsubscribeFromTopic,
  unsubscribeFromTopics
} from '../utils/ROS/ROSFunctions'
import '../assets/css/pose_estimate.css'

const PoseEstimate = () => {
  const context = useContext(Context)


  const [robotPosition, setRobotPosition] = useState(null)
  const [laserData, setLaserData] = useState({
    left: null,
    right: null,
    top: null,
    front: null,
    rear: null,
    generic: null
  })
  const [isConnected, setIsConnected] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [obstacleAlerts, setObstacleAlerts] = useState({
    left: false,
    right: false,
    top: false,
    front: false,
    rear: false,
    generic: false
  })
  const [minDistances, setMinDistances] = useState({
    left: null,
    right: null,
    top: null,
    front: null,
    rear: null,
    generic: null
  })
  const [isPaused, setIsPaused] = useState(false)
  const [fps, setFps] = useState(0)
  const [dataStats, setDataStats] = useState({
    totalUpdates: 0,
    startTime: Date.now()
  })
  const [mapData, setMapData] = useState(null)
  const [mapScale, setMapScale] = useState(1)
  const [mapOffset, setMapOffset] = useState({ x: 0, y: 0 })
  const [backgroundMapImage, setBackgroundMapImage] = useState(null)
  const [backgroundMapLoaded, setBackgroundMapLoaded] = useState(false)
  const [showLaserConfig, setShowLaserConfig] = useState(false)
  
  // Control state for which lasers are displayed on the map
  const [laserVisibility, setLaserVisibility] = useState({
    front: true,
    rear: true,
    left: true,
    right: true,
    top: true,
    generic: true
  })
  
  // Laser transformation settings (laser_link relative to base_footprint)
  const [laserTransforms, setLaserTransforms] = useState({
    front: { x: 0.3, y: 0, yaw: 0 },
    rear: { x: -0.3, y: 0, yaw: Math.PI },
    left: { x: 0, y: 0.2, yaw: Math.PI/2 },
    right: { x: 0, y: -0.2, yaw: -Math.PI/2 },
    top: { x: 0.1, y: 0, yaw: 0 },
    generic: { x: 0, y: 0, yaw: 0 }
  })

  // NEW STATES FOR "2D POSE ESTIMATE"
  const [isPoseEstimateMode, setIsPoseEstimateMode] = useState(false)
  const [poseEstimateStart, setPoseEstimateStart] = useState(null)
  const [currentMousePose, setCurrentMousePose] = useState(null)
  
  // Refs for subscribers and publishers
  const positionSubscriber = useRef(null)
  const laserSubscribers = useRef({
    left: null,
    right: null,
    top: null,
    front: null,
    rear: null,
    generic: null
  })
  const canvasRefs = useRef({
    left: null,
    right: null,
    top: null,
    front: null,
    rear: null,
    generic: null
  })
  const mapCanvasRef = useRef(null)
  const mapSubscriber = useRef(null)
  const poseEstimatePublisher = useRef(null) // NEW PUBLISHER

  // Function to connect to ROS topics
  useEffect(() => {
    if (context.selectedRobot && window.ros && window.rosConnected) {
      setIsConnected(true)
      subscribeToRobotData()
      initializePublishers() // Initialize the new publisher
    } else {
      setIsConnected(false)
      unsubscribeAll()
    }

    // Clear estimate mode when disconnecting
    return () => {
      unsubscribeAll()
      setIsPoseEstimateMode(false)
      setPoseEstimateStart(null)
    }
  }, [context.selectedRobot, window.rosConnected])

  // Initialize publishers
  const initializePublishers = () => {
    if (poseEstimatePublisher.current) {
      poseEstimatePublisher.current.unadvertise()
    }
    poseEstimatePublisher.current = createPoseEstimatePublisher()
  }
  
  // Subscribe to robot data
  const subscribeToRobotData = () => {
    // Subscribe to robot position
    subscribeToPosition()
    
    // Subscribe to laser data
    subscribeToLaserData()
    
    // Subscribe to map
    subscribeToMap()
  }

  // Subscribe to robot position
  const subscribeToPosition = () => {
    if (positionSubscriber.current) {
      unsubscribeFromTopic(positionSubscriber.current)
    }

    positionSubscriber.current = subscribeToRobotPosition(context, (message) => {
      if (!isPaused) {
        setRobotPosition(message)
        setLastUpdate(new Date())
        
        // Update statistics
        setDataStats(prev => {
          const newTotal = prev.totalUpdates + 1
          const elapsed = (Date.now() - prev.startTime) / 1000
          const currentFps = newTotal / elapsed
          
          setFps(currentFps.toFixed(1))
          
          return {
            ...prev,
            totalUpdates: newTotal
          }
        })
      }
    })
  }

  // Subscribe to map
  const subscribeToMap = () => {
    if (mapSubscriber.current) {
      unsubscribeFromTopic(mapSubscriber.current)
    }

    mapSubscriber.current = subscribeToOccupancyMap(context, (message) => {
      if (!isPaused) {
        setMapData(message)
      }
    })
  }

  // Load background map
  const loadBackgroundMap = () => {
    if (context.selectedRobot?.ip && context.selected?.map?.name) {
      const mapUrl = `http://${context.selectedRobot.ip}/maps/${context.selected.map.name}.png`
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      img.onload = () => {
        setBackgroundMapImage(img)
        setBackgroundMapLoaded(true)
      }
      
      img.onerror = (error) => {
        console.error('Error loading background map:', error)
        setBackgroundMapLoaded(false)
        setBackgroundMapImage(null)
      }
      
      img.src = mapUrl
    }
  }

  useEffect(() => {
    loadBackgroundMap()
  }, [context.selectedRobot?.ip, context.selected?.map?.name])

  // Functions to control laser offsets
  const updateLaserTransform = (laserName, property, value) => {
    setLaserTransforms(prev => ({
      ...prev,
      [laserName]: {
        ...prev[laserName],
        [property]: value
      }
    }))
  }

  const resetLaserTransform = (laserName) => {
    setLaserTransforms(prev => ({
      ...prev,
      [laserName]: { x: 0, y: 0, yaw: 0 }
    }))
  }

  const applyDefaultOffset = (laserName) => {
    const defaultOffsets = {
      front: { x: 0.3, y: 0, yaw: 0 },
      rear: { x: -0.3, y: 0, yaw: Math.PI },
      left: { x: 0, y: 0.2, yaw: Math.PI/2 },
      right: { x: 0, y: -0.2, yaw: -Math.PI/2 },
      top: { x: 0.1, y: 0, yaw: 0 },
      generic: { x: 0, y: 0, yaw: 0 }
    }
    
    setLaserTransforms(prev => ({
      ...prev,
      [laserName]: defaultOffsets[laserName] || { x: 0, y: 0, yaw: 0 }
    }))
  }

  const resetAllLaserTransforms = () => {
    const defaultTransforms = {
      front: { x: 0.3, y: 0, yaw: 0 },
      rear: { x: -0.3, y: 0, yaw: Math.PI },
      left: { x: 0, y: 0.2, yaw: Math.PI/2 },
      right: { x: 0, y: -0.2, yaw: -Math.PI/2 },
      top: { x: 0.1, y: 0, yaw: 0 },
      generic: { x: 0, y: 0, yaw: 0 }
    }
    setLaserTransforms(defaultTransforms)
  }

  const saveLaserConfiguration = () => {
    try {
      const config = {
        robotId: context.selectedRobot?.id || 'default',
        mapName: context.selected?.map?.name || 'default',
        laserTransforms: laserTransforms,
        timestamp: new Date().toISOString()
      }
      
      localStorage.setItem('rviz_laser_config', JSON.stringify(config))
      alert('Configuration saved successfully!')
    } catch (error) {
      console.error('Error saving configuration:', error)
      alert('Error saving laser configuration!')
    }
  }

  const loadLaserConfiguration = () => {
    try {
      const savedConfig = localStorage.getItem('rviz_laser_config')
      if (savedConfig) {
        const config = JSON.parse(savedConfig)
        if (config.laserTransforms) {
          setLaserTransforms(config.laserTransforms)
          alert('Configuration loaded successfully!')
        }
      } else {
        alert('No saved configuration found!')
      }
    } catch (error) {
      console.error('Error loading configuration:', error)
      alert('Error loading laser configuration!')
    }
  }

  // Subscribe to laser data
  const subscribeToLaserData = () => {
    const laserTopics = [
      { name: 'left', topic: '/scan_left_raw' },
      { name: 'right', topic: '/scan_right_raw' },
      { name: 'top', topic: '/scan_top_raw' },
      { name: 'front', topic: '/scan_front' },
      { name: 'rear', topic: '/scan_rear' },
      { name: 'generic', topic: '/scan' }
    ]

    laserTopics.forEach(({ name, topic }) => {
      if (laserSubscribers.current[name]) {
        unsubscribeFromTopic(laserSubscribers.current[name])
      }

      laserSubscribers.current[name] = subscribeToLaserTopic(context, name, topic, (laserName, message) => {
        if (!isPaused) {
          const laserInfo = {
            ranges: message.ranges,
            angle_min: message.angle_min,
            angle_max: message.angle_max,
            angle_increment: message.angle_increment,
            range_min: message.range_min,
            range_max: message.range_max,
            header: message.header
          }
          
          setLaserData(prev => ({ ...prev, [laserName]: laserInfo }))

          const validRanges = message.ranges.filter(range => range > message.range_min && range < message.range_max)
          
          if (validRanges.length > 0) {
            const minDistance = Math.min(...validRanges)
            const dangerThreshold = 0.5
            setMinDistances(prev => ({ ...prev, [laserName]: minDistance }))
            setObstacleAlerts(prev => ({ ...prev, [laserName]: minDistance < dangerThreshold }))
          }
        }
      })
    })
  }

  // Disconnect from all subscribers and publishers
  const unsubscribeAll = () => {
    unsubscribeFromTopic(positionSubscriber.current)
    positionSubscriber.current = null
    
    unsubscribeFromTopic(mapSubscriber.current)
    mapSubscriber.current = null
    
    unsubscribeFromTopics(laserSubscribers.current)
    Object.keys(laserSubscribers.current).forEach(key => {
      laserSubscribers.current[key] = null
    })
    
    if (poseEstimatePublisher.current) {
        poseEstimatePublisher.current.unadvertise();
        poseEstimatePublisher.current = null;
    }
  }

  // Draw the occupancy map
  const drawMap = (mapMessage) => {
    const canvas = mapCanvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    const { width, height } = canvas
    
    ctx.clearRect(0, 0, width, height)
    ctx.fillStyle = '#f0f0f0'
    ctx.fillRect(0, 0, width, height)


    if (mapMessage) {
     drawOccupancyGrid(ctx, mapMessage, width, height)
    }

     // Draw visual feedback for pose estimation mode
    if (isPoseEstimateMode && poseEstimateStart) {
        drawPoseEstimateFeedback(ctx);
    }
  }

  // Draw background map
  const drawBackgroundMap = (ctx, canvasWidth, canvasHeight) => {
    if (!backgroundMapImage) return

    const imageAspect = backgroundMapImage.width / backgroundMapImage.height
    const canvasAspect = canvasWidth / canvasHeight
    
    let drawWidth, drawHeight, offsetX, offsetY
    
    if (imageAspect > canvasAspect) {
      drawWidth = canvasWidth * 0.9
      drawHeight = drawWidth / imageAspect
    } else {
      drawHeight = canvasHeight * 0.9
      drawWidth = drawHeight * imageAspect
    }
    offsetX = (canvasWidth - drawWidth) / 2
    offsetY = (canvasHeight - drawHeight) / 2

    // Store scale and offset for coordinate conversion
    if (mapData) { // Prioritize ROS map resolution if available
        const mapResolution = mapData.info.resolution;
        setMapScale(drawWidth / (mapData.info.width * mapResolution));
    } else {
        // Estimate scale if only PNG is available. MAY NEED ADJUSTMENT.
        // Assuming the PNG map width corresponds to a certain distance in meters.
        const estimatedMapWidthInMeters = 50; // Example: 50 meters
        setMapScale(drawWidth / estimatedMapWidthInMeters);
    }

    setMapOffset({ x: offsetX, y: offsetY })
    
    // Apply vertical flip to background map
    ctx.save()
    ctx.translate(offsetX + drawWidth / 2, offsetY + drawHeight / 2)
    ctx.scale(1, -1) // Vertical flip
    ctx.drawImage(backgroundMapImage, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight)
    ctx.restore()
    
    ctx.fillStyle = '#333'
    ctx.font = '14px Arial'
    ctx.fillText(`Background Map: ${context.selected?.map?.name || 'N/A'} (Vertical Flip)`, 10, 25)
  }

  // Draw OccupancyGrid data
  const drawOccupancyGrid = (ctx, mapMessage, canvasWidth, canvasHeight) => {
    const mapWidth = mapMessage.info.width
    const mapHeight = mapMessage.info.height
    const resolution = mapMessage.info.resolution
    const data = mapMessage.data

    const scaleX = canvasWidth / (mapWidth * resolution)
    const scaleY = canvasHeight / (mapHeight * resolution)
    const scale = Math.min(scaleX, scaleY) * 0.9
    
    setMapScale(scale)
    
    const offsetX = (canvasWidth - (mapWidth * resolution * scale)) / 2
    const offsetY = (canvasHeight - (mapHeight * resolution * scale)) / 2
    setMapOffset({ x: offsetX, y: offsetY })

    const imageData = ctx.createImageData(mapWidth, mapHeight)
      
    for (let i = 0; i < data.length; i++) {
        const val = data[i];
        const r = val === -1 ? 128 : (val === 0 ? 255 : 0);
        const g = val === -1 ? 128 : (val === 0 ? 255 : 0);
        const b = val === -1 ? 128 : (val === 0 ? 255 : 0);

        imageData.data[i * 4] = r;
        imageData.data[i * 4 + 1] = g;
        imageData.data[i * 4 + 2] = b;
        imageData.data[i * 4 + 3] = 255;
    }
    
    // Create a temporary canvas for drawing and then scaling
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = mapWidth;
    tempCanvas.height = mapHeight;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.putImageData(imageData, 0, 0);

    // Apply vertical flip to ROS map as well
    ctx.save();
    ctx.translate(offsetX + (mapWidth * resolution * scale) / 2, offsetY + (mapHeight * resolution * scale) / 2);
    ctx.scale(resolution * scale, -resolution * scale); // Vertical flip on ROS map
    ctx.imageSmoothingEnabled = false; // Keep pixels sharp
    ctx.drawImage(tempCanvas, -mapWidth / 2, -mapHeight / 2);
    ctx.restore();

    ctx.fillStyle = '#333'
    ctx.font = '12px Arial'
    const startY = backgroundMapLoaded ? 45 : 25
    ctx.fillText(`ROS Map: ${mapWidth}x${mapHeight} (${resolution.toFixed(3)}m/pixel) (Vertical Flip)`, 10, startY)
  }
  
    // Draw robot and lasers on map
  const drawRobotOnMap = () => {
    const canvas = mapCanvasRef.current;
    if (!canvas || !robotPosition || !mapData) return;

    const ctx = canvas.getContext('2d');
    const resolution = mapData.info.resolution;
    const origin = mapData.info.origin;

    // Convert robot position (meters) to map pixels
    const robotMapX = (robotPosition.position.x - origin.position.x) / resolution;
    const robotMapY = mapData.info.height - ((robotPosition.position.y - origin.position.y) / resolution);

    // Convert to canvas coordinates (with scale and offset)
    const robotCanvasX = mapOffset.x + robotMapX * resolution * mapScale;
    const robotCanvasY = mapOffset.y + robotMapY * resolution * mapScale;
    
    // Draw robot
    ctx.fillStyle = '#4a90e2';
    ctx.strokeStyle = '#2c5aa0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(robotCanvasX, robotCanvasY, 8, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    
    // Draw robot orientation
    const quat = robotPosition.orientation;
    let yaw = Math.atan2(2 * (quat.w * quat.z + quat.x * quat.y), 1 - 2 * (quat.y * quat.y + quat.z * quat.z));
    
    const arrowLength = 20;
    const arrowX = robotCanvasX + Math.cos(yaw) * arrowLength;
    const arrowY = robotCanvasY - Math.sin(yaw) * arrowLength; // Canvas Y is inverted
    
    ctx.strokeStyle = '#ff6b6b';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(robotCanvasX, robotCanvasY);
    ctx.lineTo(arrowX, arrowY);
    ctx.stroke();

    // Draw lasers
    Object.entries(laserData).forEach(([laserName, laserInfo]) => {
      if (laserInfo && laserInfo.ranges && laserVisibility[laserName]) {
        drawLaserOnMap(ctx, laserInfo, robotCanvasX, robotCanvasY, yaw, laserName);
      }
    });
  }

    // Draw laser data on map
  const drawLaserOnMap = (ctx, laserInfo, robotCanvasX, robotCanvasY, robotYaw, laserName) => {
    const laserTransform = laserTransforms[laserName] || { x: 0, y: 0, yaw: 0 };
    
    // Laser position relative to robot on canvas
    const laserOffsetX = (laserTransform.x * Math.cos(robotYaw) - laserTransform.y * Math.sin(robotYaw)) * mapScale;
    const laserOffsetY = -(laserTransform.x * Math.sin(robotYaw) + laserTransform.y * Math.cos(robotYaw)) * mapScale; // Canvas Y inverted

    const laserCanvasX = robotCanvasX + laserOffsetX;
    const laserCanvasY = robotCanvasY + laserOffsetY;
    const laserWorldYaw = robotYaw + laserTransform.yaw;

    let laserColor = '#ff6b6b';
    if (laserName.includes('front')) laserColor = '#4ecdc4';
    else if (laserName.includes('rear')) laserColor = '#ffe66d';
    else if (laserName.includes('left')) laserColor = '#a8e6cf';
    else if (laserName.includes('right')) laserColor = '#ffaaa5';
    
    ctx.fillStyle = laserColor;

    laserInfo.ranges.forEach((range, index) => {
        if (range > laserInfo.range_min && range < laserInfo.range_max) {
            const angle = laserInfo.angle_min + index * laserInfo.angle_increment;
            const totalAngle = laserWorldYaw + angle;
            
            const pointX = laserCanvasX + (range * mapScale * Math.cos(totalAngle));
            const pointY = laserCanvasY - (range * mapScale * Math.sin(totalAngle)); // Canvas Y inverted
            
            ctx.beginPath();
            ctx.arc(pointX, pointY, 1.5, 0, 2 * Math.PI);
            ctx.fill();
        }
    });
  }

  // Draw laser data on individual canvas
  const drawLaserData = (laserName, laserInfo) => {
    const canvas = canvasRefs.current[laserName]
    if (!canvas || !laserInfo || !laserInfo.ranges) return

    const ctx = canvas.getContext('2d')
    const width = canvas.width
    const height = canvas.height
    
    ctx.clearRect(0, 0, width, height)
    
    const centerX = width / 2
    const centerY = height / 2
    const maxRadius = Math.min(width, height) / 2 - 20
    const scale = maxRadius / laserInfo.range_max
    const dangerThreshold = 0.5
    
    ctx.strokeStyle = '#e0e0e0'
    ctx.lineWidth = 1
    for (let i = 1; i <= 4; i++) {
      const radius = (maxRadius / 4) * i
      ctx.beginPath()
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
      ctx.stroke()
    }
    
    ctx.strokeStyle = '#ffcccc'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(centerX, centerY, dangerThreshold * scale, 0, 2 * Math.PI)
    ctx.stroke()
    
    ctx.fillStyle = '#4a90e2'
    ctx.beginPath()
    ctx.arc(centerX, centerY, 5, 0, 2 * Math.PI)
    ctx.fill()
    
    laserInfo.ranges.forEach((range, index) => {
      if (range > laserInfo.range_min && range < laserInfo.range_max) {
        const angle = laserInfo.angle_min + (index * laserInfo.angle_increment)
        const x = centerX + (range * scale * Math.cos(angle))
        const y = centerY + (range * scale * Math.sin(angle))
        
        ctx.fillStyle = range < dangerThreshold ? '#ff4757' : '#2ed573'
        ctx.beginPath()
        ctx.arc(x, y, 2, 0, 2 * Math.PI)
        ctx.fill()
      }
    })
  }
  
  // Function to control laser visibility on map
  const toggleLaserVisibility = (laserName) => {
    setLaserVisibility(prev => ({ ...prev, [laserName]: !prev[laserName] }))
  }

  const toggleAllLasers = (visible) => {
    setLaserVisibility({ front: visible, rear: visible, left: visible, right: visible, top: visible, generic: visible })
  }

  // Update visualizations
  useEffect(() => {
    // Redraw background map and occupancy grid
    drawMap(mapData)
    // Draw robot and lasers on top
    if (robotPosition) {
      drawRobotOnMap()
    }
    
    // Draw individual laser graphs
    Object.entries(laserData).forEach(([laserName, laserInfo]) => {
      if (laserInfo) {
        drawLaserData(laserName, laserInfo)
      }
    })

  }, [laserData, robotPosition, mapData, backgroundMapLoaded,  laserVisibility, poseEstimateStart, currentMousePose]) // Added dependencies for estimation mode
  
  useEffect(() => {
    if (backgroundMapLoaded) {
      drawMap(mapData)
      if (robotPosition) {
        drawRobotOnMap()
      }
    }
  }, [backgroundMapLoaded])
  
  // FUNCTIONS FOR "2D POSE ESTIMATE"
  const handleTogglePoseEstimateMode = () => {
    if (isPoseEstimateMode) {
        // Cancel mode
        setIsPoseEstimateMode(false);
        setPoseEstimateStart(null);
        setCurrentMousePose(null);
    } else {
        // Activate mode
        if (!mapData) {
            alert("A ROS map (/map) is required to estimate position.");
            return;
        }
        setIsPoseEstimateMode(true);
        setPoseEstimateStart(null);
    }
  }

  const getMousePosOnCanvas = (e) => {
    const canvas = mapCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }
  
  const handleMapMouseDown = (e) => {
    if (!isPoseEstimateMode || !mapData) return;
    const pos = getMousePosOnCanvas(e);
    if (pos) {
        setPoseEstimateStart(pos);
    }
  };

  const handleMapMouseMove = (e) => {
    if (!isPoseEstimateMode || !poseEstimateStart || !mapData) return;
    const pos = getMousePosOnCanvas(e);
    if(pos) {
      setCurrentMousePose(pos);
    }
  };

  const handleMapMouseUp = (e) => {
    if (!isPoseEstimateMode || !poseEstimateStart || !mapData) return;
    
    const endPos = getMousePosOnCanvas(e);
    if (!endPos) {
        setIsPoseEstimateMode(false);
        setPoseEstimateStart(null);
        return;
    };
    
    const resolution = mapData.info.resolution;
    const origin = mapData.info.origin;
    
    // Convert initial click position (canvas) to ROS map coordinates (meters)
    const mapX = ((poseEstimateStart.x - mapOffset.x) / (resolution * mapScale) * resolution) + origin.position.x;
    const mapY = (origin.position.y + mapData.info.height * resolution) - ((poseEstimateStart.y - mapOffset.y) / (resolution * mapScale) * resolution);

    // Calculate orientation
    const dx = endPos.x - poseEstimateStart.x;
    const dy = endPos.y - poseEstimateStart.y;
    const yaw = Math.atan2(-dy, dx); // Y is inverted on canvas
    
    // Convert yaw to quaternion
    const qz = Math.sin(yaw / 2.0);
    const qw = Math.cos(yaw / 2.0);
    
    // Create pose data
    const poseData = {
      pose: {
        position: { x: mapX, y: mapY, z: 0.0 },
        orientation: { x: 0.0, y: 0.0, z: qz, w: qw }
      },
      covariance: [
        0.25, 0.0, 0.0, 0.0, 0.0, 0.0, 
        0.0, 0.25, 0.0, 0.0, 0.0, 0.0,
        0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 
        0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 
        0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
        0.0, 0.0, 0.0, 0.0, 0.0, 0.06853891945200942
      ]
    };

    // Publish and reset mode
    if (poseEstimatePublisher.current) {
        publishPoseEstimate(poseEstimatePublisher.current, poseData);
        console.log("Published new initial pose:", poseData);
    }
    setIsPoseEstimateMode(false);
    setPoseEstimateStart(null);
    setCurrentMousePose(null);
  };

  const drawPoseEstimateFeedback = (ctx) => {
    if (!poseEstimateStart) return;

    // Draw starting point
    ctx.fillStyle = "rgba(255, 0, 0, 0.8)";
    ctx.beginPath();
    ctx.arc(poseEstimateStart.x, poseEstimateStart.y, 6, 0, 2 * Math.PI);
    ctx.fill();

    // If mouse is moving, draw orientation arrow
    if (currentMousePose) {
        ctx.strokeStyle = "rgba(255, 0, 0, 0.8)";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(poseEstimateStart.x, poseEstimateStart.y);
        ctx.lineTo(currentMousePose.x, currentMousePose.y);
        ctx.stroke();
    }
  }

  const formatPosition = (position) => {
    if (!position) return null
    
    return {
      x: position.position?.x?.toFixed(3) || 'N/A',
      y: position.position?.y?.toFixed(3) || 'N/A',
      z: position.position?.z?.toFixed(3) || 'N/A',
      orientation: {
        x: position.orientation?.x?.toFixed(3) || 'N/A',
        y: position.orientation?.y?.toFixed(3) || 'N/A',
        z: position.orientation?.z?.toFixed(3) || 'N/A',
        w: position.orientation?.w?.toFixed(3) || 'N/A'
      }
    }
  }

  const calculateLaserStats = (laserInfo) => {
    if (!laserInfo || !laserInfo.ranges) return null
    const validRanges = laserInfo.ranges.filter(range => range > laserInfo.range_min && range < laserInfo.range_max)
    if (validRanges.length === 0) return null
    const min = Math.min(...validRanges)
    const max = Math.max(...validRanges)
    const avg = validRanges.reduce((sum, val) => sum + val, 0) / validRanges.length
    return { min: min.toFixed(3), max: max.toFixed(3), avg: avg.toFixed(3) }
  }

  const formattedPosition = formatPosition(robotPosition)

  return (
    <div className="pose-estimate-container">
      <div className="container is-fluid">
        <div className="columns">
          <div className="column">
            <div className="box">
              <h3 className="title is-4">RViz - Dados do Robô em Tempo Real</h3>
              
              <div className="field">
                <div className="control">
                  <div className={`tag ${isConnected ? 'is-success' : 'is-danger'}`}>
                    {isConnected ? 'Conectado' : 'Desconectado'}
                  </div>
                  {context.selectedRobot && (
                    <div className="tag is-info ml-2">
                      {context.selectedRobot.name} ({context.selectedRobot.ROS || 'ROS1'})
                    </div>
                  )}
                  <div className="tag is-light ml-2">
                    FPS: {fps}
                  </div>
                </div>
              </div>

              <div className="field is-grouped">
                <div className="control">
                  <button 
                    className={`button ${isPaused ? 'is-success' : 'is-warning'}`}
                    onClick={() => setIsPaused(!isPaused)}
                  >
                    {isPaused ? '▶️ Retomar' : '⏸️ Pausar'}
                  </button>
                </div>
                {/* NEW 2D POSE ESTIMATE BUTTON */}
                <div className="control">
                   <button 
                    className={`button ${isPoseEstimateMode ? 'is-danger' : 'is-info'}`}
                    onClick={handleTogglePoseEstimateMode}
                    disabled={!mapData || !isConnected}
                    title={!mapData ? "Carregue um mapa ROS para usar esta função" : (isPoseEstimateMode ? "Cancelar estimativa" : "Definir posição inicial do robô no mapa")}
                  >
                    📍 {isPoseEstimateMode ? 'Cancel estimate pose' : '2D Estimate Pose'}
                  </button>
                </div>
              </div>
              
              {isPoseEstimateMode && (
                <div className="notification is-link is-light">
                  <h5 className="title is-6">Modo de Estimativa de Posição Ativo</h5>
                   <p>1. Clique e segure no mapa para definir a <strong>posição</strong> do robô.</p>
                   <p>2. Arraste e solte para definir a <strong>orientação</strong>.</p>
                </div>
              )}

              {lastUpdate && (
                <div className="field">
                  <label className="label">Última Atualização:</label>
                  <div className="control">
                    <span className="tag is-light">
                      {lastUpdate.toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
     
        <div className="columns">
          <div className="column">
            <div className="box">
              <div className="level">
                <div className="level-left">
                  <h4 className="title is-5">🗺️ Mapa de Ocupação</h4>
                </div>
              </div>
              
              {mapData || backgroundMapLoaded ? (
                <div>
                  <div className="field">
                    <div className="control">
                      <canvas
                        ref={mapCanvasRef}
                        width={800}
                        height={600}
                        style={{
                          border: '1px solid #e0e0e0',
                          backgroundColor: '#fff',
                          cursor: isPoseEstimateMode ? 'crosshair' : 'grab' // Dynamic cursor
                        }}
                        // NEW MOUSE EVENTS
                        onMouseDown={handleMapMouseDown}
                        onMouseMove={handleMapMouseMove}
                        onMouseUp={handleMapMouseUp}
                      />
                    </div>
                  </div>
                  
                  <div className="field">
                    <div className="content">
                        {/* Caption and other controls remain here */}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="notification is-light">
                  <div className="content">
                    <h6 className="subtitle is-6">🔍 Aguardando dados do mapa...</h6>
                     <p>Para usar o "2D Pose Estimate", é necessário conectar e carregar um mapa do tópico ROS <code>/map</code>.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>



      </div>
    </div>
  )
}

export default PoseEstimate;